/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useMemo } from "react";
import styles from "./helpButton.module.scss";
import Typography from "../../../reusableComponent/typography/Typography";
import { colors } from "../../../../styles/design-tokens";

interface HelpButtonProps {
  borderColor?: string;
  icon?: string;
  title?: string;
  subTitle?: string;
  onClick?: () => void;
  phoneCall?: boolean;
  email?: boolean;
  secondaryPhone?: string;
}

// Helper function to send phone call message to iOS native app
const sendIOSPhoneMessage = (phoneNumber: string): void => {
  if (window?.parent) {
    const message = {
      action: "tel",
      data: { phoneNumber },
    };
    window.parent.postMessage(JSON.stringify(message));
  }
};

// Helper component for phone number rendering
const PhoneLink: React.FC<{
  phoneNumber: string;
  subTitle?: string;
  isIOSNativeApp: boolean;
  onClick: () => void;
  showSubTitle?: boolean;
}> = ({ phoneNumber, subTitle, isIOSNativeApp, onClick, showSubTitle = true }) => {
  const handleClick = () => {
    onClick();
    if (isIOSNativeApp) {
      sendIOSPhoneMessage(phoneNumber);
    }
  };

  const content = (
    <>
      <Typography size="size14" color="neutral" weight={600}>
        {phoneNumber}
      </Typography>
      {showSubTitle && subTitle && (
        <Typography size="size10" color="neutral" weight={600}>
          {subTitle}
        </Typography>
      )}
    </>
  );

  return isIOSNativeApp ? (
    <div className={styles.titleContainer} onClick={handleClick} role="button">
      {content}
    </div>
  ) : (
    <a href={`tel:${phoneNumber}`} className={styles.titleContainer} onClick={handleClick} role="button">
      {content}
    </a>
  );
};

export default function HelpButton({
  icon = "",
  title = "Email Us",
  subTitle = "",
  borderColor = colors.brand.primary,
  onClick = () => {},
  phoneCall = false,
  email = false,
  secondaryPhone = "",
}: HelpButtonProps) {
  // Memoize the iOS native app check to avoid DOM query on every render
  const isIOSNativeApp = useMemo(() => {
    const nativeElement = document.getElementById("iosNative") as HTMLInputElement | null;
    return nativeElement?.value === "true";
  }, []);

  // Email button variant
  if (email) {
    return (
      <a className={styles.main} onClick={onClick} role="button" aria-label={`${title} - ${subTitle}`}>
        <div className={styles.iconContainer} style={{ outlineColor: borderColor }}>
          <img className={styles.icon} src={icon} alt={title} style={{ height: "23px" }} />
        </div>
        <div className={styles.titleContainer}>
          <Typography size="size14" color="neutral" weight={600}>
            {title}
          </Typography>
          <Typography size="size10" color="neutral" weight={600}>
            {subTitle}
          </Typography>
        </div>
      </a>
    );
  }

  // Phone call button variant
  if (phoneCall) {
    return (
      <div className={styles.main}>
        <div className={styles.iconContainer} style={{ outlineColor: borderColor }}>
          <img className={styles.icon} src={icon} alt={title} style={{ height: "25px" }} />
        </div>
        <div className={styles.phoneContainer}>
          <PhoneLink phoneNumber={title} subTitle={subTitle} isIOSNativeApp={isIOSNativeApp} onClick={onClick} />
          {secondaryPhone && (
            <PhoneLink
              phoneNumber={secondaryPhone}
              isIOSNativeApp={isIOSNativeApp}
              onClick={onClick}
              showSubTitle={false}
            />
          )}
        </div>
      </div>
    );
  }

  // Default button variant
  return (
    <div className={styles.main} onClick={onClick} role="button">
      <div className={styles.iconContainer} style={{ outlineColor: borderColor }}>
        <img className={styles.icon} src={icon} alt={title} style={{ height: "25px" }} />
      </div>
      <div className={styles.titleContainer}>
        <Typography size="size14" color="neutral" weight={600}>
          {title}
        </Typography>
        <Typography size="size10" color="neutral" weight={600}>
          {subTitle}
        </Typography>
      </div>
    </div>
  );
}
